import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  submitOnboardingRequest,
} from "../../redux/Thunk/Doctor/DoctorThunks";
import toast from "react-hot-toast";
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import firebaseApp from "../../firebase";
import apiAxios from "../../services/apiAxios";

const MORNING_SLOTS = [
  "06:00 AM", "06:30 AM", "07:00 AM", "07:30 AM", "08:00 AM", "08:30 AM", "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM"
];

const EVENING_SLOTS = [
  "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM", "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM", "05:00 PM", "05:30 PM",
  "06:00 PM", "06:30 PM", "07:00 PM", "07:30 PM", "08:00 PM", "08:30 PM", "09:00 PM", "09:30 PM", "10:00 PM", "10:30 PM", "11:00 PM", "11:30 PM"
];

const CustomDropdown = ({ value, onChange, options, placeholder = "Select" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 rounded-xl border border-web-border/60 hover:border-web-primary focus:border-web-primary outline-none text-xs bg-white text-web-text flex items-center justify-between transition-all"
      >
        <span className="truncate">{value || placeholder}</span>
        <svg
          className={`w-3 h-3 text-web-text/50 transition-transform duration-200 shrink-0 ml-1 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-web-border/40 rounded-xl shadow-lg scrollbar-hide">
          <div className="py-1 animate-fadeIn">
            <button
              type="button"
              onClick={() => {
                onChange("");
                setIsOpen(false);
              }}
              className="w-full px-3 py-1.5 text-left text-xs text-red-500 hover:bg-red-50/50 font-medium transition-colors border-b border-web-border/10"
            >
              Clear
            </button>
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  onChange(opt);
                  setIsOpen(false);
                }}
                className={`w-full px-3 py-1.5 text-left text-xs hover:bg-web-border/10 transition-colors ${value === opt ? "bg-web-primary/10 text-web-primary font-semibold" : "text-web-text"
                  }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SmartClinicOnboarding = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state.doctor);
  const [step, setStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isResolvingMap, setIsResolvingMap] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    contactNumber: "",
    personalEmail: "",
    qualificationSpecialisation: "",
    registrationNumber: "",
    experience: "",
    aboutYou: "",
    platformUsage: "",
    clinicName: "",
    tagline: "",
    operatorName: "",
    managerName: "",
    aboutClinic: "",
    clinicContactNumber: "",
    clinicEmail: "",
    premisesNo: "",
    floor: "",
    premisesName: "",
    landmark: "",
    areaRoad: "",
    city: "",
    state: "",
    pinCode: "",
    googleMapUrl: "",
    paymentOption: "Bank Transfer",
    bankAccountNumber: "",
    accountName: "",
    bankName: "",
    ifscName: "",
    upiId: "",
    opdTimings: "",
    morningStart: "",
    morningEnd: "",
    eveningStart: "",
    eveningEnd: "",
    averageDailyPatients: "",
    percentPrescribedMedicines: "",
    averageMedicinesPerPrescription: "",
    averagePrescriptionCost: "",
    prescribedMedicinesList: "",
    gstNumber: "",
  });

  // OTP Authentication States
  const [firebaseIdToken, setFirebaseIdToken] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const confirmationResultRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  const createRecaptchaDOM = () => {
    let container = document.getElementById("recaptcha-container");
    if (!container) {
      container = document.createElement("div");
      container.id = "recaptcha-container";
      container.style.position = "fixed";
      container.style.bottom = "20px";
      container.style.right = "20px";
      container.style.zIndex = "100000";
      document.body.appendChild(container);
    }
    return { container, modal: null };
  };

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (step === 1 && !isSubmitted) {
      createRecaptchaDOM();
    } else {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          console.error("Error clearing recaptcha verifier:", e);
        }
        recaptchaVerifierRef.current = null;
      }
      const container = document.getElementById("recaptcha-container");
      if (container) {
        container.remove();
      }
    }

    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          console.error("Error clearing recaptcha verifier on unmount:", e);
        }
        recaptchaVerifierRef.current = null;
      }
      const container = document.getElementById("recaptcha-container");
      if (container) {
        container.remove();
      }
    };
  }, [step, isSubmitted]);

  const handleSendOtp = async () => {
    const phone = formData.contactNumber.trim();
    if (!phone || phone.length < 10) {
      toast.error("Please enter a valid 10-digit contact number first.");
      return;
    }

    setIsVerifyingOtp(true);
    const toastId = toast.loading("Sending verification code...");
    try {
      const auth = getAuth(firebaseApp);
      const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

      // Enable App Verification Bypass for testing on localhost ONLY for test numbers
      const isTestNumber = phone.replace(/[^\d+]/g, "").endsWith("9999999999");
      if (isLocalhost && isTestNumber) {
        auth.settings.appVerificationDisabledForTesting = true;
      } else {
        auth.settings.appVerificationDisabledForTesting = false;
      }

      // Synchronously ensure reCAPTCHA DOM exists and is displayed BEFORE rendering or verification
      const { container } = createRecaptchaDOM();
      if (container) {
        container.innerHTML = ""; // Clear any previous rendered iframes to prevent "already been rendered" error
      }

      // Ensure recaptcha verifier is ready
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) { }
        recaptchaVerifierRef.current = null;
      }

      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => { },
        "expired-callback": () => {
          toast.error("reCAPTCHA expired. Please try sending OTP again.");
        }
      });
      await recaptchaVerifierRef.current.render();

      // Format number to carry +91 country code prefix (assuming India)
      let formattedPhone = phone;
      if (!formattedPhone.startsWith("+")) {
        formattedPhone = formattedPhone.replace(/^0+/, "");
        if (formattedPhone.length === 10) {
          formattedPhone = `+91${formattedPhone}`;
        }
      }

      // Prevent infinite hanging when reCAPTCHA service is blocked or unreachable (e.g. by adblockers)
      const authPromise = signInWithPhoneNumber(auth, formattedPhone, recaptchaVerifierRef.current);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("reCAPTCHA connection timed out. If you are using an adblocker (like uBlock Origin or Brave Shields), please disable it for localhost and refresh/try again.")), 15000)
      );

      const confirmationResult = await Promise.race([authPromise, timeoutPromise]);
      confirmationResultRef.current = confirmationResult;
      setOtpSent(true);
      setCountdown(60);
      toast.success("Verification code sent to " + formattedPhone, { id: toastId });
    } catch (error) {
      console.error("Firebase signInWithPhoneNumber error:", error);
      try {
        console.log("Firebase Auth Error Details JSON:", JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
      } catch (e) {
        console.error("Failed to serialize error object:", e);
      }
      toast.error("Failed to send OTP: " + (error.message || "unknown error"), { id: toastId });

      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) { }
        recaptchaVerifierRef.current = null;
      }
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Please enter the 6-digit OTP code.");
      return;
    }

    if (!confirmationResultRef.current) {
      toast.error("No active OTP session. Please send OTP first.");
      return;
    }

    setIsVerifyingOtp(true);
    const toastId = toast.loading("Verifying code...");
    try {
      const result = await confirmationResultRef.current.confirm(otpCode);
      const token = await result.user.getIdToken();
      setFirebaseIdToken(token);
      toast.success("Phone number verified successfully!", { id: toastId });
      setStep(2);
    } catch (error) {
      console.error("Firebase confirmation confirm error:", error);
      toast.error("Invalid verification code. Please check and try again.", { id: toastId });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const formContainer = document.getElementById("form-scroll-container");
    if (formContainer) {
      formContainer.scrollTop = 0;
    }
  }, [step]);

  const handleMapUrlResolve = async (url) => {
    if (!url || !url.trim().startsWith("http")) return;

    setIsResolvingMap(true);
    const toastId = toast.loading("Autofetching clinic address details...");
    try {
      const response = await apiAxios.post("api/doctor/onboard/resolve-map", { url });
      const address = response.data;

      setFormData((prev) => ({
        ...prev,
        googleMapUrl: url,
        clinicName: address.clinicName || prev.clinicName,
        premisesNo: address.premisesNo || prev.premisesNo,
        floor: address.floor || prev.floor,
        premisesName: address.premisesName || prev.premisesName,
        landmark: address.landmark || prev.landmark,
        areaRoad: address.areaRoad || prev.areaRoad,
        city: address.city || prev.city,
        state: address.state || prev.state,
        pinCode: address.pinCode || prev.pinCode,
      }));

      toast.success("Address details autofilled! Please verify and complete any missing fields.", { id: toastId });
    } catch (error) {
      console.error("Error resolving Google Map URL:", error);
      toast.error(error.response?.data?.error || "Could not autofetch address from link. Please enter manually.", { id: toastId });
    } finally {
      setIsResolvingMap(false);
    }
  };

  const handleTimeChange = (name, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      let combined = "";
      const isFullDay = updated.morningStart && updated.eveningEnd && !updated.morningEnd && !updated.eveningStart;

      if (isFullDay) {
        combined = `Full Day: ${updated.morningStart} - ${updated.eveningEnd}`;
      } else {
        let morningPart = "";
        if (updated.morningStart && updated.morningEnd) {
          morningPart = `Morning: ${updated.morningStart} - ${updated.morningEnd}`;
        }

        let eveningPart = "";
        if (updated.eveningStart && updated.eveningEnd) {
          eveningPart = `Evening: ${updated.eveningStart} - ${updated.eveningEnd}`;
        }

        if (morningPart && eveningPart) {
          combined = `${morningPart} & ${eveningPart}`;
        } else if (morningPart) {
          combined = morningPart;
        } else if (eveningPart) {
          combined = eveningPart;
        }
      }

      updated.opdTimings = combined;
      return updated;
    });
  };



  const totalSteps = 7;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.contactNumber.trim()) return "Please enter your contact number.";
      if (!firebaseIdToken) return "Please verify your mobile number with OTP first.";
    } else if (step === 2) {
      if (!formData.name.trim()) return "Please enter your name.";
      if (!formData.surname.trim()) return "Please enter your surname.";
      if (!formData.qualificationSpecialisation.trim()) return "Please enter your qualification/specialisation.";
      if (!formData.registrationNumber.trim()) return "Please enter your registration number.";
      if (!formData.experience.trim()) return "Please enter your years of experience.";
      if (!formData.platformUsage) return "Please select how you plan to use our platform.";
    } else if (step === 3) {
      if (!formData.clinicName.trim()) return "Please enter your clinic name.";
      if (!formData.premisesNo.trim()) return "Please enter premises number.";
      if (!formData.premisesName.trim()) return "Please enter building/premises name.";
      if (!formData.areaRoad.trim()) return "Please enter street/road/area.";
      if (!formData.city.trim()) return "Please enter city.";
      if (!formData.state.trim()) return "Please enter state.";
      if (!formData.pinCode.trim()) return "Please enter pincode.";
      if (!formData.googleMapUrl.trim()) return "Please enter your Google Map Link.";
    } else if (step === 4) {
      const { morningStart, morningEnd, eveningStart, eveningEnd } = formData;

      const hasMorning = morningStart && morningEnd;
      const hasEvening = eveningStart && eveningEnd;
      const hasFullDay = morningStart && eveningEnd && !morningEnd && !eveningStart;

      // Incomplete shifts validation
      if (morningStart && !morningEnd && !eveningEnd) {
        return "Please select End Time for Morning shift (or Evening shift for Full Day).";
      }
      if (!morningStart && morningEnd) {
        return "Please select Start Time for Morning shift.";
      }
      if (eveningStart && !eveningEnd) {
        return "Please select End Time for Evening shift.";
      }
      if (eveningEnd && !eveningStart && !morningStart) {
        return "Please select Start Time for Evening shift (or Morning shift for Full Day).";
      }
      if (morningEnd && eveningEnd && !morningStart) {
        return "Please select Start Time for Morning shift.";
      }
      if (morningEnd && eveningStart && !morningStart) {
        return "Please select Start Time for Morning shift.";
      }
      if (morningEnd && eveningEnd && !eveningStart) {
        return "Please select Start Time for Evening shift.";
      }

      // Ensure at least one complete block exists
      if (!hasMorning && !hasEvening && !hasFullDay) {
        return "Please select OPD timings for at least Morning, Evening, or Full Day.";
      }
      if (!formData.averageDailyPatients) return "Please select average daily patients.";
      if (!formData.percentPrescribedMedicines) return "Please select percentage of patients prescribed medicines.";
    } else if (step === 5) {
      if (!formData.prescribedMedicinesList.trim()) return "Please list at least one commonly prescribed medicine or brand.";
    } else if (step === 6) {
      if (!formData.paymentOption) return "Please select a payout method.";
      if (formData.paymentOption === "Bank Transfer") {
        if (!formData.bankAccountNumber.trim()) return "Please enter bank account number.";
        if (!formData.accountName.trim()) return "Please enter account holder name.";
        if (!formData.bankName.trim()) return "Please enter bank name.";
        if (!formData.ifscName.trim()) return "Please enter IFSC code.";
      } else if (formData.paymentOption === "UPI") {
        if (!formData.upiId.trim()) return "Please enter your UPI ID.";
      }
    }
    return null;
  };

  const handleNext = () => {
    const errorMsg = validateStep();
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }
    setStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handlePrev = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    const errorMsg = validateStep();
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }

    try {
      await dispatch(submitOnboardingRequest({ ...formData, firebaseIdToken })).unwrap();
      setIsSubmitted(true);
      setStep(7);
    } catch (err) {
      // toast shown by thunk
    }
  };

  const slideVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: "easeOut" } },
    exit: { opacity: 0, x: -30, transition: { duration: 0.3, ease: "easeIn" } },
  };

  return (
    <div className="fixed inset-0 bg-web-bg text-web-text font-web-sans flex flex-col md:flex-row selection:bg-web-primary selection:text-white overflow-hidden overscroll-none">

      {/* Left Column: Branding & Value Proposition (Minimal, non-distracting) */}
      <div className="w-full md:w-[35%] bg-white border-b md:border-b-0 md:border-r border-web-border/60 p-5 md:p-8 lg:p-12 flex flex-row md:flex-col justify-between items-center md:items-start shrink-0 relative z-30 overflow-hidden shadow-sm md:shadow-none">
        {/* Background Accent Gradient Blobs */}
        <div className="absolute top-[-200px] left-[-200px] w-[400px] h-[400px] bg-web-secondary/15 rounded-full blur-[80px] -z-10 pointer-events-none" />
        <div className="absolute bottom-[-200px] right-[-200px] w-[400px] h-[400px] bg-web-primary/5 rounded-full blur-[80px] -z-10 pointer-events-none" />

        {/* Logo */}
        <div className="flex items-center gap-3">
          <Link to="/partner" className="flex items-center gap-3 hover:opacity-85 transition-all">
            <img src="/logo.png" alt="hyperapt" className="h-8 w-auto" />
            <span className="font-web-serif font-bold text-2xl sm:text-[30px] text-web-text">
              hyperapt
            </span>
          </Link>
        </div>

        <div className="md:hidden bg-web-primary/10 text-web-primary text-[10px] uppercase font-bold tracking-wider px-3 py-1 rounded-full whitespace-nowrap">
          Smart Onboarding
        </div>

        {/* Key Info Points */}
        <div className="my-12 space-y-8 max-w-sm hidden md:block">
          <div>
            <h1 className="font-web-serif text-3xl lg:text-4xl font-bold leading-tight text-web-text mb-4">
              Step Into Your <span className="text-web-primary italic font-semibold">Smart Clinic</span>
            </h1>
            <p className="text-sm lg:text-base text-web-text/60 leading-relaxed font-light">
              Join India's premier digital consultation and instant pharmacy network. Fill out the application details to customize your clinic pharmacy setup.
            </p>
          </div>

          <div className="space-y-4 pt-4 border-t border-web-border/50">
            <div className="flex items-start gap-3.5">
              <div className="w-5 h-5 rounded-full bg-web-primary/10 flex items-center justify-center text-web-primary text-xs shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="text-xs font-semibold text-web-text">Advanced Consultations</h4>
                <p className="text-[11px] text-web-text/50 font-light leading-normal">Open patient's HealthMap — full history, past prescriptions, reports in one view.</p>
              </div>
            </div>
            <div className="flex items-start gap-3.5">
              <div className="w-5 h-5 rounded-full bg-web-primary/10 flex items-center justify-center text-web-primary text-xs shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="text-xs font-semibold text-web-text">Digital Prescriptions & Receipts</h4>
                <p className="text-[11px] text-web-text/50 font-light leading-normal">Prescription generated and sent directly to patient's phone. No paper, no file.</p>
              </div>
            </div>
            <div className="flex items-start gap-3.5">
              <div className="w-5 h-5 rounded-full bg-web-primary/10 flex items-center justify-center text-web-primary text-xs shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="text-xs font-semibold text-web-text">Fulfilled in-clinic in-minutes</h4>
                <p className="text-[11px] text-web-text/50 font-light leading-normal">Patient waits in clinic, medicines arrive in just 10–15 mins. </p>
              </div>
            </div>
          </div>
        </div>

        {/* Back Link */}
        <div className="text-xs text-web-text/40 font-light flex items-center gap-1.5 hidden md:flex">
          <span>Need help? Contact support at</span>
          <a href="mailto:partner@hyperapt.com" className="text-web-primary hover:underline font-medium">partner@hyperapt.com</a>
        </div>
      </div>

      <div className="flex-1 flex flex-col h-full bg-[#FAF9F5] overflow-hidden">

        {/* Step progress & header */}
        {!isSubmitted && (
          <div className="bg-[#FAF9F5] py-3 px-4 sm:px-6 lg:px-12 border-b border-web-border/20 w-full shrink-0 z-20">
            <div className="w-full max-w-2xl mx-auto">
              <div className="flex items-center justify-between text-xs text-web-text/50 font-medium mb-2">
                <span>STEP {step} OF {totalSteps - 1}</span>
                <span className="text-web-primary font-bold">
                  {Math.round(((step - 1) / (totalSteps - 1)) * 100)}% COMPLETE
                </span>
              </div>
              <div className="w-full bg-web-border/20 h-1.5 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }}
                  className="bg-web-primary h-full rounded-full"
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          </div>
        )}

        <div id="form-scroll-container" className="flex-1 flex flex-col justify-between p-4 pb-24 sm:p-6 lg:p-12 overflow-y-auto">

          {/* Onboarding Panel Card */}
          <div className="max-w-2xl mx-auto w-full flex-1 flex flex-col justify-center py-6">
            <div className="bg-white border border-web-border/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-10 shadow-xl shadow-black/[0.02]">
              <AnimatePresence mode="wait">
                {!isSubmitted && step === 1 && (
                  <motion.div
                    key="step1"
                    variants={slideVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="space-y-6"
                  >
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold font-web-serif text-web-text">Contact Verification</h3>
                      <p className="text-xs sm:text-sm text-web-text/50 font-light mt-0.5">Verify your mobile number to set it as your Login ID & begin onboarding</p>
                    </div>

                    <div className="flex flex-col gap-1">
                      <div className="flex justify-between items-center">
                        <label className="text-xs font-semibold text-web-text/77">Contact Number *</label>
                        {firebaseIdToken && (
                          <span className="text-[10px] text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded-full flex items-center gap-1 animate-fade-in">
                            Verified ✓
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="tel"
                          name="contactNumber"
                          value={formData.contactNumber}
                          onChange={handleChange}
                          disabled={!!firebaseIdToken || otpSent}
                          placeholder="10-digit mobile number"
                          className="flex-1 px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                        />
                        {!firebaseIdToken && !otpSent && (
                          <button
                            type="button"
                            onClick={handleSendOtp}
                            disabled={isVerifyingOtp || !formData.contactNumber || formData.contactNumber.trim().length < 10}
                            className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-web-primary text-white text-xs font-semibold hover:bg-web-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[90px] text-center"
                          >
                            Send OTP
                          </button>
                        )}
                        {!firebaseIdToken && otpSent && (
                          <button
                            type="button"
                            onClick={() => {
                              setOtpSent(false);
                              setCountdown(0);
                            }}
                            className="w-full sm:w-auto px-3 py-2.5 rounded-xl border border-web-border/80 text-web-text/60 text-xs font-semibold hover:bg-web-border/10 transition-colors text-center min-w-[90px]"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>

                    {!firebaseIdToken && otpSent && (
                      <div className="bg-[#FAF9F5] border border-web-border/40 rounded-xl p-4 space-y-3 animate-fade-in">
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-web-text/70">Enter 6-Digit OTP *</label>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <input
                              type="text"
                              maxLength={6}
                              value={otpCode}
                              onChange={(e) => setOtpCode(e.target.value.replace(/[^\d]/g, ""))}
                              placeholder="e.g. 123456"
                              className="flex-1 px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-white font-mono tracking-widest text-center"
                            />
                            <button
                              type="button"
                              onClick={handleVerifyOtp}
                              disabled={isVerifyingOtp || otpCode.length !== 6}
                              className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[90px] text-center"
                            >
                              Verify OTP
                            </button>
                          </div>
                        </div>

                        <div className="flex justify-between items-center text-xs text-web-text/50">
                          <span>Didn't receive code?</span>
                          {countdown > 0 ? (
                            <span>Resend in {countdown}s</span>
                          ) : (
                            <button
                              type="button"
                              onClick={handleSendOtp}
                              className="text-web-primary font-semibold hover:underline"
                            >
                              Resend OTP
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {!isSubmitted && step === 2 && (
                  <motion.div
                    key="step2"
                    variants={slideVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="space-y-6"
                  >
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold font-web-serif text-web-text">Personal Details & Credentials</h3>
                      <p className="text-xs sm:text-sm text-web-text/50 font-light mt-0.5">Provide credentials to verify your medical practice</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">First Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="e.g. Rajesh"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Surname / Last Name *</label>
                        <input
                          type="text"
                          name="surname"
                          value={formData.surname}
                          onChange={handleChange}
                          placeholder="e.g. Kumar"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-semibold text-web-text/70">Personal Email</label>
                      <input
                        type="email"
                        name="personalEmail"
                        value={formData.personalEmail}
                        onChange={handleChange}
                        placeholder="e.g. rajesh.kumar@gmail.com"
                        className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="flex flex-col gap-1 sm:col-span-2">
                        <label className="text-xs font-semibold text-web-text/70">Qualification & Specialisation *</label>
                        <input
                          type="text"
                          name="qualificationSpecialisation"
                          value={formData.qualificationSpecialisation}
                          onChange={handleChange}
                          placeholder="e.g. MBBS, MD Internal Medicine"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Experience (Years) *</label>
                        <input
                          type="number"
                          name="experience"
                          value={formData.experience}
                          onChange={handleChange}
                          placeholder="e.g. 10"
                          min="0"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-semibold text-web-text/70">Registration Number *</label>
                      <input
                        type="text"
                        name="registrationNumber"
                        value={formData.registrationNumber}
                        onChange={handleChange}
                        placeholder="State/National medical council registration number"
                        className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-semibold text-web-text/70">About Yourself (Bio)</label>
                      <textarea
                        name="aboutYou"
                        value={formData.aboutYou}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Tell us briefly about your medical background..."
                        className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] resize-none transition-colors"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-semibold text-web-text/70">
                        How will you be using our platform? *
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[
                          { id: "Consultation Management and Smart Pharmacy", label: "Consultation Management & Smart Pharmacy" },
                          { id: "Only Smart Pharmacy", label: "Only Smart Pharmacy" },
                          { id: "Only Consultation Management", label: "Only Consultation Management" },
                          { id: "Not sure! would decide after starting out", label: "Not sure! Decide after starting out" },
                        ].map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => handleSelect("platformUsage", opt.id)}
                            className={`p-3 border text-xs text-left rounded-xl transition-all flex items-center justify-between gap-3 ${formData.platformUsage === opt.id
                              ? "bg-web-primary/10 border-web-primary text-web-primary font-medium"
                              : "border-web-border/80 text-web-text/70 bg-[#FCFBFA] hover:bg-web-border/10"
                              }`}
                          >
                            <span>{opt.label}</span>
                            {formData.platformUsage === opt.id && (
                              <svg className="w-4 h-4 text-web-primary" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {!isSubmitted && step === 3 && (
                  <motion.div
                    key="step3"
                    variants={slideVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="space-y-5"
                  >
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold font-web-serif text-web-text">Clinic Address & Location</h3>
                      <p className="text-xs sm:text-sm text-web-text/50 font-light mt-0.5">Specify where you want the clinic pharmacy setup</p>
                    </div>

                    <div className="flex flex-col gap-1 bg-[#FAF9F5] border border-web-border/40 p-4 rounded-2xl space-y-2">
                      <label className="text-xs font-bold text-web-text/80">Google Map Location *</label>
                      <input
                        type="url"
                        name="googleMapUrl"
                        value={formData.googleMapUrl}
                        onChange={handleChange}
                        onBlur={(e) => handleMapUrlResolve(e.target.value)}
                        placeholder="Paste your Google Map Share URL to autofill address..."
                        className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-white transition-colors w-full font-light"
                      />
                      <p className="text-[10px] text-web-text/40 font-light leading-normal">
                        Pasting your clinic's Google Map link will automatically extract and autofill the clinic name, door no, building name, landmark, road, city, and pincode!
                      </p>
                      {isResolvingMap && (
                        <div className="text-xs text-web-primary font-semibold flex items-center gap-1.5 pt-1 animate-pulse">
                          <div className="w-2 h-2 bg-web-primary rounded-full animate-ping" />
                          Autofetching clinic address from link...
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Clinic Name *</label>
                        <input
                          type="text"
                          name="clinicName"
                          value={formData.clinicName}
                          onChange={handleChange}
                          placeholder="e.g. Care Clinic & Wellness"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Tagline / Subtext</label>
                        <input
                          type="text"
                          name="tagline"
                          value={formData.tagline}
                          onChange={handleChange}
                          placeholder="e.g. Your health is our priority"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Premises No *</label>
                        <input
                          type="text"
                          name="premisesNo"
                          value={formData.premisesNo}
                          onChange={handleChange}
                          placeholder="e.g. Shop G-10"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Floor</label>
                        <input
                          type="text"
                          name="floor"
                          value={formData.floor}
                          onChange={handleChange}
                          placeholder="e.g. Ground Floor"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Premises Name *</label>
                        <input
                          type="text"
                          name="premisesName"
                          value={formData.premisesName}
                          onChange={handleChange}
                          placeholder="e.g. Horizon Chambers"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Landmark</label>
                        <input
                          type="text"
                          name="landmark"
                          value={formData.landmark}
                          onChange={handleChange}
                          placeholder="e.g. Opposite Central Park"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Street / Road Name *</label>
                        <input
                          type="text"
                          name="areaRoad"
                          value={formData.areaRoad}
                          onChange={handleChange}
                          placeholder="e.g. Linking Road"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">City *</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          placeholder="e.g. Mumbai"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">State *</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          placeholder="e.g. Maharashtra"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Pin Code *</label>
                        <input
                          type="text"
                          name="pinCode"
                          value={formData.pinCode}
                          onChange={handleChange}
                          placeholder="6-digit pincode"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-web-border/30">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Clinic Contact</label>
                        <input
                          type="text"
                          name="clinicContactNumber"
                          value={formData.clinicContactNumber}
                          onChange={handleChange}
                          placeholder="Clinic phone number"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Clinic Email</label>
                        <input
                          type="email"
                          name="clinicEmail"
                          value={formData.clinicEmail}
                          onChange={handleChange}
                          placeholder="contact@clinicname.com"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {!isSubmitted && step === 4 && (
                  <motion.div
                    key="step4"
                    variants={slideVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="space-y-6"
                  >
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold font-web-serif text-web-text">OPD Details & Operations</h3>
                      <p className="text-xs sm:text-sm text-web-text/50 font-light mt-0.5">Provide details on OPD footfalls and timings</p>
                    </div>

                    <div className="space-y-3">
                      <label className="text-xs font-semibold text-web-text/70 block">OPD Timings *</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Morning OPD Card */}
                        <div className="p-4 rounded-2xl border border-web-border/40 bg-[#FAF9F5] space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-web-text/80">Morning OPD</span>
                            {(formData.morningStart || formData.morningEnd) && (
                              <button
                                type="button"
                                onClick={() => {
                                  handleTimeChange("morningStart", "");
                                  handleTimeChange("morningEnd", "");
                                }}
                                className="text-[10px] text-red-500 hover:underline font-medium"
                              >
                                Clear Morning
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="flex flex-col gap-1">
                              <label className="text-[10px] font-semibold text-web-text/50">Start Time</label>
                              <CustomDropdown
                                value={formData.morningStart}
                                onChange={(val) => handleTimeChange("morningStart", val)}
                                options={MORNING_SLOTS}
                                placeholder="Select"
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-[10px] font-semibold text-web-text/50">End Time</label>
                              <CustomDropdown
                                value={formData.morningEnd}
                                onChange={(val) => handleTimeChange("morningEnd", val)}
                                options={MORNING_SLOTS}
                                placeholder="Select"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Evening OPD Card */}
                        <div className="p-4 rounded-2xl border border-web-border/40 bg-[#FAF9F5] space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-web-text/80">Evening OPD</span>
                            {(formData.eveningStart || formData.eveningEnd) && (
                              <button
                                type="button"
                                onClick={() => {
                                  handleTimeChange("eveningStart", "");
                                  handleTimeChange("eveningEnd", "");
                                }}
                                className="text-[10px] text-red-500 hover:underline font-medium"
                              >
                                Clear Evening
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="flex flex-col gap-1">
                              <label className="text-[10px] font-semibold text-web-text/50">Start Time</label>
                              <CustomDropdown
                                value={formData.eveningStart}
                                onChange={(val) => handleTimeChange("eveningStart", val)}
                                options={EVENING_SLOTS}
                                placeholder="Select"
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-[10px] font-semibold text-web-text/50">End Time</label>
                              <CustomDropdown
                                value={formData.eveningEnd}
                                onChange={(val) => handleTimeChange("eveningEnd", val)}
                                options={EVENING_SLOTS}
                                placeholder="Select"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2.5">
                      <label className="text-xs font-semibold text-web-text/70">Average Daily Patient OPD Footfall? *</label>
                      <div className="grid grid-cols-2 gap-2">
                        {["Less than 10", "10 to 20", "20 to 50", "More than 50"].map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => handleSelect("averageDailyPatients", opt)}
                            className={`p-3 rounded-xl border text-center transition-all font-medium text-xs ${formData.averageDailyPatients === opt
                              ? "bg-web-primary border-web-primary text-white shadow-sm"
                              : "border-web-border bg-white hover:bg-web-border/10 text-web-text"
                              }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2.5">
                      <label className="text-xs font-semibold text-web-text/70">What % of patients are prescribed medicines? *</label>
                      <div className="grid grid-cols-2 gap-2">
                        {["0 - 25%", "25 - 50%", "50 - 75%", "75 - 100%"].map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => handleSelect("percentPrescribedMedicines", opt)}
                            className={`p-3 rounded-xl border text-center transition-all font-medium text-xs ${formData.percentPrescribedMedicines === opt
                              ? "bg-web-primary border-web-primary text-white shadow-sm"
                              : "border-web-border bg-white hover:bg-web-border/10 text-web-text"
                              }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Avg Meds Per Prescription (Optional)</label>
                        <input
                          type="text"
                          name="averageMedicinesPerPrescription"
                          value={formData.averageMedicinesPerPrescription}
                          onChange={handleChange}
                          placeholder="e.g. 2 to 3"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-web-text/70">Avg Prescription Cost (Optional)</label>
                        <input
                          type="text"
                          name="averagePrescriptionCost"
                          value={formData.averagePrescriptionCost}
                          onChange={handleChange}
                          placeholder="e.g. Rs. 200 - Rs. 500"
                          className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-semibold text-web-text/70">GST Number (Optional)</label>
                      <input
                        type="text"
                        name="gstNumber"
                        value={formData.gstNumber}
                        onChange={handleChange}
                        placeholder="Enter 15-digit GSTIN"
                        className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] transition-colors text-uppercase"
                      />
                    </div>
                  </motion.div>
                )}

                {!isSubmitted && step === 5 && (
                  <motion.div
                    key="step5"
                    variants={slideVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="space-y-6"
                  >
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold font-web-serif text-web-text">Medicines Inventory Planning</h3>
                      <p className="text-xs sm:text-sm text-web-text/50 font-light mt-0.5">Specify medicine brands or categories you commonly prescribe</p>
                    </div>

                    <div className="bg-web-secondary/10 p-4 rounded-xl border border-web-secondary/20 text-xs text-web-text/70 leading-relaxed font-light">
                      We keep medicines stocked locally to fulfill your prescriptions in minutes. Please list your most commonly prescribed medicines, drugs or any other products below.
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-semibold text-web-text/70">Commonly Prescribed Medicines/Brands *</label>
                      <textarea
                        name="prescribedMedicinesList"
                        value={formData.prescribedMedicinesList}
                        onChange={handleChange}
                        rows="7"
                        placeholder="e.g. Pantocid 40mg, Augmentin 625, Montair LC, Crocin 650, Telma 40, Glycomet GP2, etc."
                        className="px-4 py-3 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] resize-none transition-colors"
                      />
                    </div>
                  </motion.div>
                )}

                {!isSubmitted && step === 6 && (
                  <motion.div
                    key="step6"
                    variants={slideVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="space-y-6"
                  >
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold font-web-serif text-web-text">Settlement & Payout Details</h3>
                      <p className="text-xs sm:text-sm text-web-text/50 font-light mt-0.5">Direct direct payout settings for pharmacy share settlements</p>
                    </div>

                    <div className="flex gap-4">
                      {["Bank Transfer", "UPI"].map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => handleSelect("paymentOption", opt)}
                          className={`flex-1 p-3 rounded-xl border text-center font-semibold text-xs transition-all ${formData.paymentOption === opt
                            ? "bg-web-primary border-web-primary text-white shadow-sm"
                            : "border-web-border bg-white hover:bg-web-border/10 text-web-text"
                            }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>

                    <AnimatePresence mode="wait">
                      {formData.paymentOption === "Bank Transfer" && (
                        <motion.div
                          key="bank"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-4 overflow-hidden"
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-web-text/70">Bank Account Number *</label>
                              <input
                                type="text"
                                name="bankAccountNumber"
                                value={formData.bankAccountNumber}
                                onChange={handleChange}
                                placeholder="Account number"
                                className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA]"
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-web-text/70">Account Holder Name *</label>
                              <input
                                type="text"
                                name="accountName"
                                value={formData.accountName}
                                onChange={handleChange}
                                placeholder="Account name"
                                className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA]"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-web-text/70">Bank Name *</label>
                              <input
                                type="text"
                                name="bankName"
                                value={formData.bankName}
                                onChange={handleChange}
                                placeholder="e.g. HDFC Bank"
                                className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA]"
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-web-text/70">IFSC Code *</label>
                              <input
                                type="text"
                                name="ifscName"
                                value={formData.ifscName}
                                onChange={handleChange}
                                placeholder="e.g. HDFC0001234"
                                className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA] text-uppercase"
                              />
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {formData.paymentOption === "UPI" && (
                        <motion.div
                          key="upi"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="flex flex-col gap-1 overflow-hidden"
                        >
                          <label className="text-xs font-semibold text-web-text/70">UPI ID *</label>
                          <input
                            type="text"
                            name="upiId"
                            value={formData.upiId}
                            onChange={handleChange}
                            placeholder="doctorname@okaxis"
                            className="px-4 py-2.5 rounded-xl border border-web-border/80 focus:border-web-primary outline-none text-sm bg-[#FCFBFA]"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )}

                {isSubmitted && step === 7 && (
                  <motion.div
                    key="step7"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1, transition: { type: "spring", stiffness: 100 } }}
                    className="flex flex-col items-center justify-center text-center py-6 space-y-6"
                  >
                    <div className="w-16 h-16 rounded-full bg-green-50 text-green-500 flex items-center justify-center shadow-lg shadow-green-50/50">
                      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-xl sm:text-2xl font-bold font-web-serif text-web-text">Onboarding Application Filed</h3>
                      <p className="text-xs text-web-text/50 font-light max-w-md mx-auto leading-relaxed">
                        Thank you Dr. {formData.name} {formData.surname}. Your onboarding application request has been received. Our operations team will verify your clinic location and update your registered account.
                      </p>
                    </div>

                    <div className="border border-web-border/50 rounded-2xl p-5 bg-[#FAF9F5] max-w-sm text-left space-y-2.5 shadow-sm text-xs w-full">
                      <span className="font-semibold text-web-text uppercase tracking-wider text-[10px]">What happens next?</span>
                      <p className="text-web-text/60 leading-normal font-light">
                        1. Our representative will contact you at <strong>{formData.contactNumber}</strong> to coordinate setup.<br />
                        2. Upon approval, PIN credentials will be sent to your registered phone number to login.
                      </p>
                    </div>

                    <button
                      onClick={() => navigate("/partner")}
                      className="px-8 py-3 rounded-full bg-web-primary text-white font-semibold text-xs shadow-lg shadow-web-primary/20 hover:shadow-web-primary/40 transition-all"
                    >
                      Return to website
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Navigation Buttons */}
          {!isSubmitted && (
            <div className="max-w-2xl mx-auto w-full pt-4 border-t border-web-border/30 flex items-center justify-between">
              <button
                onClick={handlePrev}
                disabled={step === 1}
                className={`px-5 py-2 rounded-full border text-xs font-semibold transition-all ${step === 1
                  ? "border-web-border/30 text-web-text/20 cursor-not-allowed"
                  : "border-web-border hover:bg-web-border/10 text-web-text"
                  }`}
              >
                Back
              </button>

              {step < totalSteps - 1 ? (
                <button
                  onClick={handleNext}
                  className="px-6 py-2 rounded-full bg-web-primary text-white text-xs font-semibold shadow-lg shadow-web-primary/15 hover:shadow-web-primary/35 transition-all"
                >
                  Next Step
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-8 py-2.5 rounded-full bg-web-primary text-white text-xs font-bold shadow-lg shadow-web-primary/15 hover:shadow-web-primary/35 transition-all"
                >
                  {loading ? "Submitting..." : "Submit Application"}
                </button>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SmartClinicOnboarding;
